import { cloneElement, isValidElement } from "react";
import type { ReactNode } from "react";

export type FormLabelInlineHintProps = {
  title: ReactNode;
  hint: ReactNode;
  /** When true, hint renders on its own line under the title (and required *). */
  hintBelowTitle?: boolean;
  /** Injected by `separationFormRequiredMark` when the field is required. */
  fieldRequired?: boolean;
};

/**
 * Label: title, then required * (when applicable), and hint either on the same row
 * (label in the first column, hint right-aligned in the second with a fixed column gap)
 * or on the line below
 * when `hintBelowTitle` is set.
 */
export function FormLabelInlineHint({
  title,
  hint,
  hintBelowTitle = false,
  fieldRequired = false,
}: FormLabelInlineHintProps) {
  const titleRow = (
    <span className="inline-flex items-baseline gap-1 shrink-0 min-w-0">
      <span>{title}</span>
      {fieldRequired ? (
        <span
          className="text-[var(--color-error)] font-sans leading-none shrink-0"
          aria-hidden="true"
        >
          *
        </span>
      ) : null}
    </span>
  );

  if (hintBelowTitle) {
    return (
      <span className="flex flex-col gap-1 w-full min-w-0 items-stretch">
        {titleRow}
        <span className="text-xs font-normal text-slate-500 leading-snug">
          {hint}
        </span>
      </span>
    );
  }

  return (
    <span className="grid w-full min-w-0 grid-cols-[auto_1fr] items-baseline gap-x-2 gap-y-1">
      {titleRow}
      <span className="text-xs font-normal text-slate-500 leading-snug justify-self-end text-right min-w-0 max-w-[min(100%,28rem)]">
        {hint}
      </span>
    </span>
  );
}

export type FormLabelWithInlineHintOptions = {
  hintBelowTitle?: boolean;
};

/** Shorthand for `<FormLabelInlineHint title={t} hint={h} />`. */
export function formLabelWithInlineHint(
  title: ReactNode,
  hint: ReactNode,
  options?: FormLabelWithInlineHintOptions,
): ReactNode {
  return (
    <FormLabelInlineHint
      title={title}
      hint={hint}
      hintBelowTitle={options?.hintBelowTitle}
    />
  );
}

/** Used by `separationFormRequiredMark` to merge Ant Design required state into inline hints. */
export function mergeRequiredIntoLabelNode(
  labelNode: ReactNode,
  required: boolean,
): ReactNode {
  if (isValidElement(labelNode) && labelNode.type === FormLabelInlineHint) {
    return cloneElement(labelNode, { fieldRequired: required });
  }
  return labelNode;
}
